import {
  type CustomerOrderShipment,
  getOrderTrackingStatusName,
  getOrderTrackingUrl,
  ORDER_COURIERS,
} from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { formatDateTime, safeHttpsUrl } from '@/shared/lib';

interface OrderShipmentListProps {
  readonly shipments: readonly CustomerOrderShipment[];
  /** 위 작은 영문 머리말 — 배송지 카드 안에서는 카드 제목이 있어 null(머리말 없음). */
  readonly heading?: string | null;
  /** 영역 이름 — 배송지 카드는 `배송지 N 송장`으로 배송지끼리 구별한다. */
  readonly label?: string;
}

const LINK_CLASS = 'w-fit text-sm font-semibold underline underline-offset-4 hover:no-underline';

const courierNameOf = (code: string): string =>
  ORDER_COURIERS.find((courier) => courier.code === code)?.name ?? code;

/** 추적 상태 화면 이름 — 결과가 아직 없으면(추적 등록 실패 포함, 운영자가 처리한다) 조회 대기, 표에 없는 코드는 원문 대신 확인 중. */
const trackingStatusLabel = (code: string | null): string =>
  code === null ? '조회 대기' : (getOrderTrackingStatusName(code) ?? '상태 확인 중');

/**
 * 배송 송장 — 택배는 택배사·송장 번호·현재 상태(한글 이름)·진행 이력과 새 창 택배사 조회 링크(https 만), 직접 전달은 전달 방법.
 * 추적 등록 실패는 운영자 화면에만 있다(주문 spec §7-2) — 고객에게는 조회 대기로 보인다.
 * 결제 정보와 같은 이름/값 줄 모양이다.
 */
export const OrderShipmentList = ({
  shipments,
  heading = 'Delivery',
  label = '배송 송장',
}: OrderShipmentListProps): ReactElement => (
  <section aria-label={label} className="flex flex-col gap-3">
    {heading === null ? null : (
      <p
        aria-hidden
        className="text-[11px] leading-none font-semibold tracking-[0.18em] text-muted-foreground uppercase"
      >
        {heading}
      </p>
    )}
    <div className="flex flex-col divide-y divide-foreground/10 border-y border-foreground/10 text-sm">
      {shipments.map((shipment) => {
        if (shipment.kind === 'DIRECT') {
          return (
            <div key={shipment.id} className="flex flex-col gap-1 py-4">
              <p className="font-semibold">직접 전달</p>
              <p className="whitespace-pre-wrap text-muted-foreground wrap-anywhere">
                {shipment.directMethodNote}
              </p>
            </div>
          );
        }

        const trackingUrl = safeHttpsUrl(
          getOrderTrackingUrl(shipment.courierCode, shipment.trackingNumber),
        );
        return (
          <div key={shipment.id} className="flex flex-col gap-3 py-4">
            <p className="font-semibold tabular-nums">
              {courierNameOf(shipment.courierCode)} {shipment.trackingNumber}
            </p>
            <dl className="flex flex-col gap-1">
              <div className="flex gap-4">
                <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">현재 상태</dt>
                <dd>{trackingStatusLabel(shipment.trackingStatus)}</dd>
              </div>
              {shipment.trackingCheckedAt !== null ? (
                <div className="flex gap-4">
                  <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">조회 일시</dt>
                  <dd className="tabular-nums">{formatDateTime(shipment.trackingCheckedAt)}</dd>
                </div>
              ) : null}
            </dl>
            {shipment.trackingProgresses !== null && shipment.trackingProgresses.length > 0 ? (
              <ol className="flex flex-col gap-2 border-l border-foreground/15 pl-4 text-xs">
                {shipment.trackingProgresses.map((progress) => (
                  <li
                    key={`${progress.dateTime}-${progress.location}-${progress.statusCode}-${progress.description}`}
                    className="flex flex-col gap-0.5"
                  >
                    <span className="text-muted-foreground tabular-nums">
                      {formatDateTime(progress.dateTime)} · {progress.location}
                    </span>
                    <span>
                      {progress.status} {progress.description}
                    </span>
                  </li>
                ))}
              </ol>
            ) : null}
            {trackingUrl !== null ? (
              <a
                href={trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={LINK_CLASS}
              >
                택배사 조회
              </a>
            ) : null}
          </div>
        );
      })}
    </div>
  </section>
);
